import React, { useState, useMemo } from 'react';
import { BrandProfile, MonthlyPlan, ContentSlide } from '../types';
import { LayoutTemplate, Wand2, Layers, Type, FileImage } from 'lucide-react';

interface ParsedContent {
  heading: string;
  subheading: string;
  paragraphs: string[];
  bulletPoints: string[];
}

function parseSmartText(text?: string): ParsedContent {
  if (!text) return { heading: '', subheading: '', paragraphs: [], bulletPoints: [] };
  
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  if (lines.length === 0) return { heading: '', subheading: '', paragraphs: [], bulletPoints: [] };
  
  let heading = '';
  let subheading = '';
  let remainingStartIdx = 1;

  // Detect subheading (ALL CAPS or short prefix)
  if (lines.length > 1 && lines[0] === lines[0].toUpperCase() && lines[0].length < 40) {
    subheading = lines[0];
    heading = lines[1];
    remainingStartIdx = 2;
  } else {
    heading = lines[0];
  }

  // Handle explicit format "Heading: Body" pattern
  if (lines.length === 1) {
    const colonIdx = heading.indexOf(':');
    if (colonIdx > 0 && colonIdx < 50) {
      const parts = [heading.substring(0, colonIdx).trim(), heading.substring(colonIdx + 1).trim()];
      heading = parts[0];
      return { heading, subheading: '', paragraphs: [parts[1]], bulletPoints: [] };
    }
  }

  const bulletPoints: string[] = [];
  const paragraphs: string[] = [];

  for (let i = remainingStartIdx; i < lines.length; i++) {
    const line = lines[i];
    if (line.match(/^[•\-\*]/) || line.match(/^\d+\./)) {
      bulletPoints.push(line.replace(/^[•\-\*\d\.]+\s*/, '').trim());
    } else {
      paragraphs.push(line);
    }
  }

  return { heading, subheading, paragraphs, bulletPoints };
}

function detectTheme(concept: string): string {
  const c = (concept || '').toLowerCase();
  
  if (c.includes('educational tip') || c.includes('dark green') || c.includes('educational')) return 'educational-tip';
  if (c.includes('hero hook') || c.includes('magazine')) return 'magazine-diagonal';
  if (c.includes('glass') || c.includes('floating')) return 'floating-modern';
  if (c.includes('right side') || c.includes('text right')) return 'split-right';
  if (c.includes('bottom text') || c.includes('footer')) return 'bottom-story';
  
  return 'split-left'; // Safe default for "Text left / Photo Right"
}

// Sub-component for individual slide rendering
function SlideLabDisplay({ slide, slideIndex }: { slide: ContentSlide, slideIndex: number }) {
  const parsedContent = useMemo(() => parseSmartText(slide.overlayText), [slide.overlayText]);
  const defaultTheme = useMemo(() => detectTheme(slide.concept || ''), [slide.concept]);
  
  const [selectedTheme, setSelectedTheme] = useState<string>('');
  const activeTheme = selectedTheme || defaultTheme;

  const renderLayout = () => {
    const content = parsedContent;
    
    if (activeTheme === 'split-left') {
      return (
        <div className="absolute inset-y-0 left-0 w-[55%] bg-[#f8f9fa] p-8 flex flex-col justify-center shadow-2xl">
          {content.subheading && <div className="text-[10px] font-bold uppercase tracking-widest text-[#666] mb-3">{content.subheading}</div>}
          {content.heading && <h2 className="text-[1.35rem] font-serif text-gray-900 mb-5 leading-snug">{content.heading}</h2>}
          <div className="space-y-3">
            {content.paragraphs.map((p, i) => <p key={i} className="text-xs text-gray-600 leading-relaxed font-sans">{p}</p>)}
          </div>
          {content.bulletPoints.length > 0 && (
            <ul className="mt-4 space-y-2">
              {content.bulletPoints.map((b, i) => (
                 <li key={i} className="flex items-start gap-2 text-xs text-gray-700 font-sans">
                    <span className="text-indigo-600 mt-0.5">•</span>
                    <span>{b}</span>
                 </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    if (activeTheme === 'split-right') {
      return (
        <div className="absolute inset-y-0 right-0 w-[55%] bg-[#f8f9fa] p-8 flex flex-col justify-center shadow-2xl">
          {content.subheading && <div className="text-[10px] font-bold uppercase tracking-widest text-[#666] mb-3">{content.subheading}</div>}
          {content.heading && <h2 className="text-[1.35rem] font-serif text-gray-900 mb-5 leading-snug">{content.heading}</h2>}
          <div className="space-y-3">
            {content.paragraphs.map((p, i) => <p key={i} className="text-xs text-gray-600 leading-relaxed font-sans">{p}</p>)}
          </div>
          {content.bulletPoints.length > 0 && (
            <ul className="mt-4 space-y-2">
              {content.bulletPoints.map((b, i) => (
                 <li key={i} className="flex items-start gap-2 text-xs text-gray-700 font-sans">
                    <span className="text-indigo-600 mt-0.5">•</span>
                    <span>{b}</span>
                 </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    if (activeTheme === 'educational-tip') {
      return (
        <div className="absolute inset-y-0 left-0 w-[55%] bg-[#778a78] p-8 flex flex-col justify-center shadow-2xl overflow-y-auto">
          {content.subheading && <div className="text-[10px] font-bold uppercase tracking-widest text-[#d8e3d9] mb-3">{content.subheading}</div>}
          {content.heading && <h2 className="text-[1.35rem] font-bold font-sans text-white mb-5 leading-snug">{content.heading}</h2>}
          <div className="space-y-3">
            {content.paragraphs.map((p, i) => <p key={i} className="text-xs text-[#eff4f0] leading-relaxed font-sans">{p}</p>)}
          </div>
          {content.bulletPoints.length > 0 && (
            <ul className="mt-4 space-y-2">
              {content.bulletPoints.map((b, i) => (
                 <li key={i} className="flex items-start gap-2 text-xs text-white font-sans font-medium">
                    <span className="text-white mt-0.5 font-bold">•</span>
                    <span>{b}</span>
                 </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    if (activeTheme === 'magazine-diagonal') {
      return (
        <>
          <div className="absolute inset-0 bg-black/15"></div>
          <div className="absolute top-6 left-6 right-12 drop-shadow-md">
            {content.subheading && <div className="text-[10px] font-bold uppercase tracking-widest text-white/90 mb-2">{content.subheading}</div>}
            {content.heading && <h2 className="text-[1.75rem] font-serif font-bold text-white leading-tight max-w-[85%]">{content.heading}</h2>}
          </div>
          {(content.paragraphs.length > 0 || content.bulletPoints.length > 0) && (
            <div className="absolute bottom-6 right-6 w-[60%] bg-white/95 backdrop-blur-md p-5 rounded-2xl shadow-xl max-h-[50%] overflow-y-auto">
              <div className="space-y-2">
                {content.paragraphs.map((p, i) => <p key={i} className="text-[11px] text-gray-800 leading-relaxed font-medium font-sans">{p}</p>)}
              </div>
              {content.bulletPoints.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200/60">
                  <ul className="space-y-1.5">
                    {content.bulletPoints.map((b, i) => (
                       <li key={i} className="flex items-start gap-2 text-[11px] text-gray-700 font-sans">
                          <span className="text-indigo-600 mt-0.5">•</span>
                          <span>{b}</span>
                       </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      );
    }

    if (activeTheme === 'floating-modern') {
      return (
        <>
          {content.heading && (
            <div className="absolute top-6 left-1/2 -translate-x-1/2 w-[80%] bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-lg text-center">
              {content.subheading && <div className="text-[9px] font-bold uppercase tracking-widest text-gray-500 mb-1">{content.subheading}</div>}
              <h2 className="text-xl font-serif text-gray-900 leading-tight">{content.heading}</h2>
            </div>
          )}
          {(content.paragraphs.length > 0 || content.bulletPoints.length > 0) && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[80%] bg-white/95 backdrop-blur-md p-5 rounded-xl shadow-lg max-h-[50%] overflow-y-auto">
              <div className="space-y-2">
                {content.paragraphs.map((p, i) => <p key={i} className="text-[11px] text-gray-600 leading-relaxed font-sans">{p}</p>)}
              </div>
              {content.bulletPoints.length > 0 && (
                <ul className="mt-3 grid grid-cols-1 gap-1.5">
                  {content.bulletPoints.map((b, i) => (
                     <li key={i} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-100 text-[10px] text-gray-700 font-medium font-sans">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0"></span>
                        <span>{b}</span>
                     </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      );
    }

    // bottom-story
    return (
      <>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent pt-16 pb-6 px-6">
          {content.subheading && <div className="text-[10px] font-bold uppercase tracking-widest text-gray-300 mb-2">{content.subheading}</div>}
          {content.heading && <h2 className="text-xl font-serif font-bold text-white mb-3 leading-snug">{content.heading}</h2>}
          <div className="space-y-2">
            {content.paragraphs.map((p, i) => <p key={i} className="text-[11px] text-gray-200 leading-relaxed font-sans">{p}</p>)}
          </div>
          {content.bulletPoints.length > 0 && (
            <ul className="mt-3 grid grid-cols-2 gap-2">
              {content.bulletPoints.map((b, i) => (
                 <li key={i} className="flex items-start gap-2 text-[10px] text-gray-300 font-sans">
                    <span className="text-white mt-0.5">•</span>
                    <span>{b}</span>
                 </li>
              ))}
            </ul>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col min-w-[500px] w-[500px] bg-white rounded-[2rem] shadow-sm border border-gray-200 overflow-hidden snap-start flex-shrink-0 relative">
       {/* Preview Canvas */}
       {slide.backgroundSliceUrl || slide.url ? (
         <div className="w-full aspect-square relative bg-gray-100 group overflow-hidden">
           <img src={slide.backgroundSliceUrl || slide.url} alt={`Slide ${slideIndex + 1}`} className="w-full h-full object-cover" />
           {renderLayout()}
         </div>
       ) : (
         <div className="w-full aspect-square bg-gray-100 flex items-center justify-center text-gray-400">
           <div className="text-center p-6 flex flex-col items-center">
              <FileImage className="w-8 h-8 opacity-50 mb-2" />
              <div className="mb-2">No Image Generated Yet</div>
           </div>
         </div>
       )}

       {/* Editor Panel - Now purely informational / overriding */}
       <div className="bg-[#f8f9fa] flex-1 flex flex-col overflow-hidden max-h-[400px]">
          <div className="p-6 overflow-y-auto space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                <Wand2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm text-gray-900">Smart Layout Engine</h3>
                <p className="text-xs text-gray-500">Automatically parses & places content</p>
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider block mb-2 flex items-center gap-1.5">
                <LayoutTemplate className="w-3.5 h-3.5 text-indigo-500" /> Visual Template Map
              </label>
              <select 
                className="w-full text-sm font-medium border-gray-200 rounded-xl py-2.5 px-3 focus:ring-indigo-500 bg-white shadow-sm"
                value={activeTheme}
                onChange={e => setSelectedTheme(e.target.value)}
              >
                <option value="split-left">Text Left Column (Off-white)</option>
                <option value="split-right">Text Right Column (Off-white)</option>
                <option value="educational-tip">Educational Tip (Sage Green)</option>
                <option value="magazine-diagonal">Separated Blocks / Magazine</option>
                <option value="floating-modern">Floating Glass Cards</option>
                <option value="bottom-story">Feature Banner Bottom</option>
              </select>
              <p className="text-[10px] text-gray-500 mt-2 ml-1 leading-snug">
                The engine analyzes the visual concept (<span className="italic">"{slide.concept?.substring(0,25)}..."</span>) and automatically routes it to the closest matching HTML structural template.
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <h4 className="text-[11px] font-bold text-gray-700 uppercase tracking-wider block mb-4 flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-indigo-500" /> Extracted Elements
              </h4>
              
              <div className="space-y-4">
                {parsedContent.subheading && (
                  <div>
                    <div className="text-[9px] uppercase font-bold text-gray-400 mb-1 flex items-center gap-1"><Type className="w-3 h-3" /> Subheading Bubble</div>
                    <div className="text-xs bg-gray-50 p-2 rounded text-gray-700 border border-gray-100">{parsedContent.subheading}</div>
                  </div>
                )}
                
                {parsedContent.heading && (
                  <div>
                    <div className="text-[9px] uppercase font-bold text-gray-400 mb-1 flex items-center gap-1"><Type className="w-3 h-3" /> Heading Element</div>
                    <div className="text-sm font-bold bg-gray-50 p-2 rounded text-gray-900 border border-gray-100">{parsedContent.heading}</div>
                  </div>
                )}
                
                {(parsedContent.paragraphs.length > 0 || parsedContent.bulletPoints.length > 0) && (
                  <div>
                    <div className="text-[9px] uppercase font-bold text-gray-400 mb-1 flex items-center gap-1"><Layers className="w-3 h-3" /> Body Text Blocks ({parsedContent.paragraphs.length + parsedContent.bulletPoints.length})</div>
                    <div className="text-xs bg-gray-50 p-2.5 rounded text-gray-700 border border-gray-100 max-h-[120px] overflow-y-auto space-y-2">
                      {parsedContent.paragraphs.map((p, i) => <p key={`p-${i}`} className="leading-relaxed">{p}</p>)}
                      {parsedContent.bulletPoints.length > 0 && (
                        <ul className="space-y-1 mt-2">
                          {parsedContent.bulletPoints.map((b, i) => <li key={`b-${i}`} className="flex items-start gap-1.5"><span className="text-indigo-400">•</span><span>{b}</span></li>)}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
       </div>
    </div>
  );
}

export function HtmlOverlayLab({ brand, brands, plans }: { brand: BrandProfile | null; brands: BrandProfile[]; plans: MonthlyPlan[] }) {
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');

  const selectedPlan = useMemo(() => {
    return plans.find(p => p.id === selectedPlanId) || null;
  }, [plans, selectedPlanId]);

  const getPlanName = (p: MonthlyPlan) => {
    const brandForPlan = brands?.find(b => b.id === p.brandId);
    const brandName = brandForPlan ? brandForPlan.companyName : 'Unknown Brand';
    return `${brandName} - ${p.month} ${p.year} (${p.title || 'Untitled'})`;
  };

  return (
    <div className="space-y-8">
      <header>
        <h2 className="text-3xl font-bold text-gray-900">HTML Layout Lab</h2>
        <p className="text-gray-500 mt-1">Smart Automation Testing Environment (Restricted to dewaldjordaan5@gmail.com)</p>
      </header>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select a Plan to Process</label>
          <select
            className="w-full px-4 py-3 rounded-2xl border-gray-200 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50/50"
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
          >
            <option value="">Select Plan...</option>
            {plans.map(p => (
              <option key={p.id} value={p.id}>{getPlanName(p)}</option>
            ))}
          </select>
        </div>

        {selectedPlan ? (
          <div className="mt-8 space-y-12">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Post Rendering Automation</h3>
            <p className="text-gray-500 mb-8 max-w-3xl leading-relaxed">
              This space demonstrates the <strong className="text-gray-900">Smart Layout Engine</strong>. It automatically parses text blocks from Gemini into independent UI elements (Subheadings, Headings, Paragraphs, Lists), and intelligently routes them into structural HTML templates based on the required post format and visual concept—protecting users from manual design complexities.
            </p>

            {selectedPlan.calendar.map((post, postIndex) => (
              <div key={post.id} className="border border-gray-200 rounded-[2rem] overflow-hidden bg-gray-50/50 mb-12 shadow-sm">
                <div className="p-8 bg-white border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h4 className="text-xl font-black text-gray-900">Post {postIndex + 1}: {post.concept?.substring(0, 80) || 'Post'}...</h4>
                    <p className="text-sm text-gray-500 mt-1.5 font-medium uppercase tracking-wider">{post.format} • {post.date}</p>
                  </div>
                </div>
                
                <div className="p-8 overflow-hidden">
                  {post.format?.toLowerCase() === 'carousel' && post.slides && post.slides.length > 0 ? (
                    <div className="flex gap-6 overflow-x-auto pb-6 snap-x pt-2">
                      {post.slides.map((slide, slideIndex) => (
                        <SlideLabDisplay key={slideIndex} slide={slide} slideIndex={slideIndex} />
                      ))}
                    </div>
                  ) : (
                    <div className="w-full mx-auto flex justify-center">
                       {(post.slides && post.slides.length > 0) ? (
                         <SlideLabDisplay slide={post.slides[0]} slideIndex={0} />
                       ) : (
                         <SlideLabDisplay slide={{ id: 'single', concept: post.concept || '', url: '', overlayText: post.concept }} slideIndex={0} />
                       )}
                    </div>
                  )}
                </div>
              </div>
            ))}

          </div>
        ) : (
          <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 text-gray-500 mt-8">
            Select a plan from the dropdown to continue.
          </div>
        )}
      </div>
    </div>
  );
}
